import React, { useState } from "react";
import { Send, MapPin, Phone, Mail, CheckCircle2 } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = "Name is required";

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email address";
    }

    if (!formData.message.trim()) errors.message = "Message is required";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    setSubmitError("");
    setSubmitSuccess(false);

    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);
      const apiUrl = `${process.env.REACT_APP_CONTACT_US_API_URL}/api/contact`;

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            companyName: formData.company,
            message: formData.message,
            source: "MNR-AI-Worker",
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setSubmitSuccess(true);
          setFormData({
            name: "",
            email: "",
            phone: "",
            company: "",
            message: "",
          });
        } else {
          setSubmitError(data.message || "Failed to send message. Please try again.");
          console.error("API Error:", data);
        }
      } catch (err) {
        setSubmitError("An unexpected network error occurred.");
        console.error("Submit error:", err);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const inputClass = (name) =>
    `w-full bg-slate-50 border ${
      formErrors[name] ? "border-red-500" : "border-slate-200"
    } rounded-lg px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 transition-all duration-200 text-sm`;

  return (
    <section className="bg-slate-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-800 tracking-tight">
            Get in <span className="text-cyan-500">Touch</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Ready to integrate <span className="font-semibold text-slate-800">AI Employees</span> into your workflow? Reach out to us today.
          </p>
        </div>

        {/* Contact Layout Grid */}
        <div className="mt-16 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Info Card (Left) */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-8 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Contact Information</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-8">
                Connect with our team to explore platform integration and deployment options.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 border-2 border-blue-400 rounded-md shrink-0">
                    <MapPin size={20} className="text-cyan-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Location</h4>
                    <p className="text-slate-600 text-xs leading-relaxed mt-1">
                      WeWork - Krishe Emerald, Ground Floor, Laxmi Cyber City, HITECH City, Hyderabad - 500082
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 border-2 border-blue-400 rounded-md shrink-0">
                    <Phone size={20} className="text-cyan-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Phone</h4>
                    <p className="text-slate-600 text-xs mt-1">+91 73299 99968</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 border-2 border-blue-400 rounded-md shrink-0">
                    <Mail size={20} className="text-cyan-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Email</h4>
                    <p className="text-slate-600 text-xs mt-1">info@mnrtechnologies.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trial Info Box */}
            <div className="mt-8 p-4 bg-cyan-50 border border-cyan-100 rounded-lg text-center">
              <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wider block">Trial Included</span>
              <p className="text-sm font-bold text-slate-800 mt-1">14-day free trial for every user</p>
            </div>
          </div>

          {/* Form Card (Right - Spans 2 cols) */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-slate-100 p-8 sm:p-10">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">Send Us a Message</h3>

            {submitSuccess && (
              <div className="mb-6 bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 rounded-lg flex items-center gap-3 text-sm font-medium">
                <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                <span>Thank you! Your message has been sent successfully.</span>
              </div>
            )}

            {submitError && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg text-sm font-medium">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Full Name <span className="text-cyan-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClass("name")}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Email Address <span className="text-cyan-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass("email")}
                  />
                  {formErrors.email && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass("phone")}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  name="company"
                  placeholder="Acme Inc."
                  value={formData.company}
                  onChange={handleChange}
                  className={inputClass("company")}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Your Message <span className="text-cyan-500">*</span>
                </label>
                <textarea
                  rows="5"
                  name="message"
                  placeholder="How can we help your business?"
                  value={formData.message}
                  onChange={handleChange}
                  className={`${inputClass("message")} resize-none`}
                />
                {formErrors.message && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 px-6 rounded-lg font-bold flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <span>SEND MESSAGE</span>
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;